import { Add, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';

const AdvancedFilters = ({
  filters,
  onApply,
  onReset,
  fields = [],
  sx = {}
}) => {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters || []);

  useEffect(() => {
    setLocalFilters(filters || []);
  }, [filters]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddFilter = () => {
    setLocalFilters([...localFilters, { field: '', operator: 'contains', value: '' }]);
  };

  const handleRemoveFilter = (index) => {
    const newFilters = localFilters.filter((_, i) => i !== index);
    setLocalFilters(newFilters);
    onApply(newFilters);
  };

  const handleFilterChange = (index, field, value) => {
    const newFilters = [...localFilters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onApply(localFilters);
    handleClose();
  };

  const handleReset = () => {
    setLocalFilters([]);
    onReset();
    handleClose();
  };

  const operators = [
    { value: 'contains', label: 'Содержит' },
    { value: 'equals', label: 'Равно' },
    { value: 'startsWith', label: 'Начинается с' },
    { value: 'endsWith', label: 'Заканчивается на' },
    { value: 'greaterThan', label: 'Больше' },
    { value: 'lessThan', label: 'Меньше' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, ...sx }}>
        <Button
          variant="outlined"
          onClick={handleOpen}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            }
          }}
        >
          Расширенные фильтры
        </Button>
        {filters && filters.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.map((filter, index) => (
              <Chip
                key={index}
                label={`${fields.find(f => f.value === filter.field)?.label || filter.field} ${operators.find(op => op.value === filter.operator)?.label || filter.operator} ${filter.value}`}
                onDelete={() => handleRemoveFilter(index)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            color: 'white',
          }
        }}
      >
        <DialogTitle>
          Расширенные фильтры
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {localFilters.map((filter, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Поле</InputLabel>
                  <Select
                    value={filter.field}
                    onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                    label="Поле"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                    }}
                  >
                    {fields.map((field) => (
                      <MenuItem key={field.value} value={field.value}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Оператор</InputLabel>
                  <Select
                    value={filter.operator}
                    onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                    label="Оператор"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                    }}
                  >
                    {operators.map((operator) => (
                      <MenuItem key={operator.value} value={operator.value}>
                        {operator.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Значение"
                  value={filter.value}
                  onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />

                <IconButton
                  onClick={() => handleRemoveFilter(index)}
                  sx={{ color: 'white' }}
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>
          ))}

          <Button
            startIcon={<Add />}
            onClick={handleAddFilter}
            variant="contained"
            color="success"
          >
            Добавить фильтр
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset} sx={{ color: 'white' }}>
            Сбросить
          </Button>
          <Button onClick={handleApply} variant="contained">
            Применить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdvancedFilters; 